import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  SignOut,
  ArrowsClockwise,
  LockKey,
  Phone,
  MapPin,
  SpinnerGap,
} from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  adminLogin,
  setAdminKey,
  getAdminKey,
  clearAdminKey,
  fetchRequests,
  fetchStats,
  updateRequestStatus,
} from "@/lib/rescueApi";
import { SERVICES, STATUS_META } from "@/data/services";

const serviceLabel = (id) => SERVICES.find((s) => s.id === id)?.label || id;
const STATUSES = Object.keys(STATUS_META);

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-sm"
      style={{ color: m.color, backgroundColor: `${m.color}1a` }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: m.color }} />
      {m.label}
    </span>
  );
};

function LoginGate({ onSuccess }) {
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { key } = await adminLogin(pass);
      setAdminKey(key);
      toast.success("Welcome to the control room.");
      onSuccess();
    } catch {
      toast.error("Invalid passcode.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-rescue-bg px-5 grain-overlay">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-rescue-surface border border-white/10 p-8"
      >
        <span className="grid place-items-center h-12 w-12 bg-rescue-primary/15 text-rescue-primary rounded-sm mb-6">
          <LockKey weight="fill" size={24} />
        </span>
        <h1 className="font-display uppercase tracking-tighter text-white text-4xl leading-none">
          Control Room
        </h1>
        <p className="text-white/55 text-sm mt-3">Enter the dispatch passcode to manage requests.</p>
        <div className="mt-6 space-y-4">
          <Input
            type="password"
            data-testid="admin-passcode-input"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            placeholder="Passcode"
            className="bg-transparent border-white/20 text-white h-12 rounded-sm focus-visible:border-rescue-primary focus-visible:ring-rescue-primary/40"
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-rescue-primary hover:brightness-110 active:scale-95 disabled:opacity-60 transition-all text-white font-semibold py-3.5 rounded-sm uppercase tracking-wide"
          >
            {loading ? <SpinnerGap className="animate-spin" size={20} /> : "Enter"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="w-full inline-flex items-center justify-center gap-2 text-white/50 hover:text-white text-sm py-2"
          >
            <ArrowLeft size={16} /> Back to site
          </button>
        </div>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqs, st] = await Promise.all([fetchRequests(filter), fetchStats()]);
      setRequests(reqs);
      setStats(st);
    } catch {
      toast.error("Session expired. Please log in again.");
      onLogout();
    } finally {
      setLoading(false);
    }
  }, [filter, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await updateRequestStatus(id, status);
      toast.success("Status updated.");
      load();
    } catch {
      toast.error("Could not update status.");
    }
  };

  const statCards = stats
    ? [
        { k: stats.total, v: "Total requests", c: "#FFFFFF" },
        { k: stats.pending, v: "Pending", c: STATUS_META.pending.color },
        { k: stats.active, v: "Active", c: STATUS_META.on_the_way.color },
        { k: stats.completed, v: "Completed", c: STATUS_META.completed.color },
      ]
    : [];

  return (
    <div className="min-h-screen bg-rescue-bg grain-overlay">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-8 w-8 bg-rescue-primary text-white font-display text-xl leading-none">
              R
            </span>
            <span className="font-display text-xl uppercase tracking-tight text-white">
              Dispatch Control
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="grid place-items-center h-10 w-10 border border-white/15 rounded-sm text-white/70 hover:text-white hover:border-white/40 transition-colors"
            >
              <ArrowsClockwise size={18} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={() => navigate("/")}
              className="hidden sm:inline-flex items-center gap-2 border border-white/15 rounded-sm text-white/70 hover:text-white px-4 h-10 text-sm"
            >
              <ArrowLeft size={16} /> Site
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 bg-rescue-primary hover:brightness-110 active:scale-95 transition-all text-white px-4 h-10 rounded-sm text-sm font-semibold uppercase"
            >
              <SignOut size={16} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-10">
        <h1 className="font-display uppercase tracking-tighter text-white text-5xl sm:text-6xl leading-none">
          Requests
        </h1>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {statCards.map((s, i) => (
            <div key={i} data-testid={`admin-stat-${i}`} className="bg-rescue-surface px-6 py-6">
              <div className="font-display text-5xl leading-none" style={{ color: s.c }}>
                {s.k}
              </div>
              <div className="text-white/55 text-sm mt-2">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="mt-8 flex items-center gap-3">
          <span className="text-white/50 text-sm uppercase tracking-widest">Filter</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger
              data-testid="admin-filter"
              className="w-56 bg-rescue-surface border-white/15 text-white rounded-sm h-11"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-rescue-surface border-white/15 text-white">
              <SelectItem value="all">All requests</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_META[s].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="mt-6 border border-white/10">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-rescue-surface border-b border-white/10 text-white/40 text-xs uppercase tracking-widest">
            <div className="col-span-3">Customer</div>
            <div className="col-span-2">Service</div>
            <div className="col-span-3">Location</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Update</div>
          </div>

          {loading && requests.length === 0 ? (
            <div className="px-5 py-16 text-center text-white/50">Loading requests…</div>
          ) : requests.length === 0 ? (
            <div data-testid="admin-empty" className="px-5 py-16 text-center text-white/50">
              No requests found for this filter.
            </div>
          ) : (
            requests.map((r) => (
              <div
                key={r.id}
                data-testid={`admin-row-${r.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-5 border-b border-white/10 last:border-0 items-center hover:bg-white/[0.03] transition-colors"
              >
                <div className="col-span-3">
                  <div className="text-white font-semibold">{r.name}</div>
                  <a
                    href={`tel:${r.phone}`}
                    className="text-white/55 text-sm flex items-center gap-1.5 mt-1 hover:text-white"
                  >
                    <Phone size={13} /> {r.phone}
                  </a>
                  <div className="text-white/35 text-xs mt-1">{r.vehicle_type}</div>
                </div>
                <div className="col-span-2">
                  <span className="text-white/85 text-sm">{serviceLabel(r.service_type)}</span>
                  {r.notes ? <div className="text-white/40 text-xs mt-1 line-clamp-2">{r.notes}</div> : null}
                </div>
                <div className="col-span-3">
                  <div className="text-white/70 text-sm flex items-start gap-1.5">
                    <MapPin size={14} className="text-rescue-primary mt-0.5 shrink-0" /> {r.location}
                  </div>
                  <div className="text-white/30 text-xs mt-1">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="col-span-2">
                  <StatusBadge status={r.status} />
                </div>
                <div className="col-span-2 md:text-right">
                  <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                    <SelectTrigger
                      data-testid={`admin-status-select-${r.id}`}
                      className="w-full md:w-40 md:ml-auto bg-transparent border-white/15 text-white rounded-sm h-10 text-sm"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-rescue-surface border-white/15 text-white">
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`status-opt-${s}`}>
                          {STATUS_META[s].label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(Boolean(getAdminKey()));

  const logout = () => {
    clearAdminKey();
    setAuthed(false);
  };

  return authed ? (
    <Dashboard onLogout={logout} />
  ) : (
    <LoginGate onSuccess={() => setAuthed(true)} />
  );
}
